import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";
import { Box, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { KrijgsmanLoboFerreiraResult } from "../../types/interfaces/methods/KrijgsmanLoboFerreiraResult";

interface KrijgsmanLoboFerreiraResultsProps {
  calculations: KrijgsmanLoboFerreiraResult[];
}

export default function KrijgsmanLoboFerreiraResults({ calculations }: Readonly<KrijgsmanLoboFerreiraResultsProps>) {
  const { t } = useTranslation();

  const formatDecimals = (value?: number | null) =>
    typeof value === "number"
      ? value.toLocaleString(undefined, { maximumFractionDigits: 3 })
      : "-";

  //GRID 1
  const grid1Columns = (t: any): GridColDef[] => [
    { 
      field: "gradient", 
      headerName: t("calculations.methods.krijgsman-lobo-ferreira.results.gradienteHidraulico"), 
      flex: 1,
      valueFormatter: (value) => formatDecimals(value), 
    },
    { 
      field: "x1dia", 
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueFormatter: (value) => formatDecimals(value),
      renderHeader: () => (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          {t("calculations.methods.krijgsman-lobo-ferreira.results.variavelX")}
          <span style={{ fontWeight: 550 }}>{t("calculations.methods.krijgsman-lobo-ferreira.results.1Dia")}</span>
        </div>
      ),
   },
    { 
      field: "x50dias", 
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueFormatter: (value) => formatDecimals(value),
      renderHeader: () => (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          {t("calculations.methods.krijgsman-lobo-ferreira.results.variavelX")}
          <span style={{ fontWeight: 550 }}>{t("calculations.methods.krijgsman-lobo-ferreira.results.50Dias")}</span>
        </div>
      ),
    },
    { 
      field: "x3500dias", 
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueFormatter: (value) => formatDecimals(value),
      renderHeader: () => (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          {t("calculations.methods.krijgsman-lobo-ferreira.results.variavelX")}
          <span style={{ fontWeight: 550 }}>{t("calculations.methods.krijgsman-lobo-ferreira.results.3500Dias")}</span>
        </div>
      ),
    },
    { 
      field: "f", 
      headerName: t("calculations.methods.krijgsman-lobo-ferreira.results.variavelF"), 
      flex: 1,
      headerAlign: "center",
      align: "center",
      valueFormatter: (value) => formatDecimals(value),
    },
  ];

  const grid1Rows = (r: any) => [
    {
      id: "base",
      type: "base",
      gradient: r.baseGradient.i,
      x1dia: r.baseGradient.klfX1dia,
      x50dias: r.baseGradient.klfX50dias,
      x3500dias: r.baseGradient.klfX3500dias,
      f: r.baseGradient.klfF,
    },
    ...r.userGradients.map((ug: any, i: number) => ({
      id: i,
      type: "user",
      gradient: ug.iKLF,
      x1dia: ug.iklfX1dia,
      x50dias: ug.iklfX50dias,
      x3500dias: ug.iklfX3500dias,
      f: ug.iklfF,
    })),
    {
      id: "avg",
      type: "avg",
      gradient: r.averages.gradienteI,
      x1dia: r.averages.variavelX1Dia,
      x50dias: r.averages.variavelX50Dias,
      x3500dias: r.averages.variavelX3500Dias,
      f: r.averages.variavelF,
    },
  ];

  //GRID 2A (1 dia)
  const gridDistance1Columns = (t: any): GridColDef[] => [
    { 
      field: "gradient", 
      headerName: t("calculations.methods.krijgsman-lobo-ferreira.results.gradienteHidraulico"), 
      flex: 1,
      valueFormatter: (value) => formatDecimals(value),
    },
    { 
      field: "mont",
      flex: 1,
      valueFormatter: (value) => formatDecimals(value),
      renderHeader: () => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {t("calculations.methods.krijgsman-lobo-ferreira.results.distancia1Dia")}
          <span style={{ fontWeight: 550 }}>{t("calculations.methods.krijgsman-lobo-ferreira.results.montante")}</span>
        </div>
      ),
    },
    { 
      field: "jus",
      flex: 1,
      valueFormatter: (value) => formatDecimals(value),
      renderHeader: () => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {t("calculations.methods.krijgsman-lobo-ferreira.results.distancia1Dia")}
          <span style={{ fontWeight: 550 }}>{t("calculations.methods.krijgsman-lobo-ferreira.results.jusante")}</span>
        </div>
      ),
    },
  ];

  const gridDistance1Rows = (r: any) => [
    { 
      id: "base", 
      gradient: r.baseGradient.i, 
      mont: r.baseGradient.klfMont1, 
      jus: r.baseGradient.klfJus1 
    },
    ...r.userGradients.map((ug: any, i: number) => ({ 
      id: i, 
      gradient: ug.iKLF, 
      mont: ug.iklfMont1, 
      jus: ug.iklfJus1 
    })),
    { 
      id: "avg", 
      gradient: r.averages.gradienteI, 
      mont: r.averages.distancia1DiaMontante, 
      jus: r.averages.distancia1DiaJusante 
    },
  ];

  //GRID 2B (50 dias)
  const gridDistance50Columns = (t: any): GridColDef[] => [
    { 
      field: "gradient", 
      headerName: t("calculations.methods.krijgsman-lobo-ferreira.results.gradienteHidraulico"), 
      flex: 1,
      valueFormatter: (value) => formatDecimals(value),
    },
    { 
      field: "mont", 
      flex: 1,
      valueFormatter: (value) => formatDecimals(value),
      renderHeader: () => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {t("calculations.methods.krijgsman-lobo-ferreira.results.distancia50Dias")}
          <span style={{ fontWeight: 550 }}>{t("calculations.methods.krijgsman-lobo-ferreira.results.montante")}</span>
        </div>
      ),
    },
    { 
      field: "jus", 
      flex: 1,
      valueFormatter: (value) => formatDecimals(value),
      renderHeader: () => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {t("calculations.methods.krijgsman-lobo-ferreira.results.distancia50Dias")}
          <span style={{ fontWeight: 550 }}>{t("calculations.methods.krijgsman-lobo-ferreira.results.jusante")}</span>
        </div>
      ),
    },
  ];

  const gridDistance50Rows = (r: any) => [
    { 
      id: "base", 
      gradient: r.baseGradient.i, 
      mont: r.baseGradient.klfMont50, 
      jus: r.baseGradient.klfJus50 
    },
    ...r.userGradients.map((ug: any, i: number) => ({
      id: i, 
      gradient: ug.iKLF, 
      mont: ug.iklfMont50, 
      jus: ug.iklfJus50 
    })),
    { 
      id: "avg", 
      gradient: r.averages.gradienteI, 
      mont: r.averages.distancia50DiasMontante, 
      jus: r.averages.distancia50DiasJusante 
    },
  ];

  //GRID 2C (3500 dias)
  const gridDistance3500Columns = (t: any): GridColDef[] => [
    { 
      field: "gradient", 
      headerName: t("calculations.methods.krijgsman-lobo-ferreira.results.gradienteHidraulico"), 
      flex: 1,
      valueFormatter: (value) => formatDecimals(value),
    },
    { 
      field: "mont", 
      flex: 1,
      valueFormatter: (value) => formatDecimals(value),
      renderHeader: () => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {t("calculations.methods.krijgsman-lobo-ferreira.results.distancia3500Dias")}
          <span style={{ fontWeight: 550 }}>{t("calculations.methods.krijgsman-lobo-ferreira.results.montante")}</span>
        </div>
      ),
    },
    { 
      field: "jus", 
      flex: 1,
      valueFormatter: (value) => formatDecimals(value),
      renderHeader: () => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {t("calculations.methods.krijgsman-lobo-ferreira.results.distancia3500Dias")}
          <span style={{ fontWeight: 550 }}>{t("calculations.methods.krijgsman-lobo-ferreira.results.jusante")}</span>
        </div>
      ),
    },
    { 
      field: "perp", 
      flex: 1,
      valueFormatter: (value) => formatDecimals(value),
      renderHeader: () => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {t("calculations.methods.krijgsman-lobo-ferreira.results.distancia3500Dias")}
          <span style={{ fontWeight: 550 }}>{t("calculations.methods.krijgsman-lobo-ferreira.results.perpendicular")}</span>
        </div>
      ),
    },
  ];

  const gridDistance3500Rows = (r: any) => [
    { 
      id: "base", 
      gradient: r.baseGradient.i, 
      mont: r.baseGradient.klfMont3500, 
      jus: r.baseGradient.klfJus3500, 
      perp: r.baseGradient.klfPerp3500 
    },
    ...r.userGradients.map((ug: any, i: number) => ({ 
      id: i, 
      gradient: ug.iKLF, 
      mont: ug.iklfMont3500, 
      jus: ug.iklfJus3500, 
      perp: ug.iklfPerp3500 
    })),
    { 
      id: "avg", 
      gradient: r.averages.gradienteI, 
      mont: r.averages.distancia3500DiasMontante, 
      jus: r.averages.distancia3500DiasJusante, 
      perp: r.averages.distanciaPerpendicular3500Dias 
    },
  ];

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
        {t("calculations.methods.results-title")}
      </Typography>

      {calculations.map((calc, index) => {
        const r = calc.results;

        return (
          <Box key={index} sx={{ mb: 6 }}>
            {/* GRID 1 */}
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ fontWeight: 700, mt: 3, mb: 1, display: "flex", alignItems: "center", gap: 1, }}
            >
              <RadioButtonCheckedIcon sx={{ fontSize: 18 }} />
              {t("calculations.methods.krijgsman-lobo-ferreira.results.grid1")}
            </Typography>
            <DataGrid
              rows={grid1Rows(r)}
              columns={grid1Columns(t).map(c => ({ ...c, sortable: false }))}
              hideFooter
              disableColumnMenu
              sx={{
                boxShadow: 1,
                "& .MuiDataGrid-row:last-child": {
                  fontWeight: 700,
                  borderTop: "3px solid",
                },
              }}
            />

            {/* GRID 2A — 1 DIA */}
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ fontWeight: 700, mt: 3, mb: 1, display: "flex", alignItems: "center", gap: 1, }}
            >
              <RadioButtonCheckedIcon sx={{ fontSize: 18 }} />
              {t("calculations.methods.krijgsman-lobo-ferreira.results.distancia1Dia")}
            </Typography>
            <DataGrid
              rows={gridDistance1Rows(r)}
              columns={gridDistance1Columns(t).map(c => ({ ...c, sortable: false }))}
              hideFooter
              disableColumnMenu
              sx={{
                boxShadow: 1,
                "& .MuiDataGrid-row:last-child": {
                  fontWeight: 700,
                  borderTop: "3px solid",
                },
              }}
            />

            {/* GRID 2B — 50 DIAS */}
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ fontWeight: 700, mt: 3, mb: 1, display: "flex", alignItems: "center", gap: 1, }}
            >
              <RadioButtonCheckedIcon sx={{ fontSize: 18 }} />
              {t("calculations.methods.krijgsman-lobo-ferreira.results.distancia50Dias")}
            </Typography>
            <DataGrid
              rows={gridDistance50Rows(r)}
              columns={gridDistance50Columns(t).map(c => ({ ...c, sortable: false }))}
              hideFooter
              disableColumnMenu
              sx={{
                boxShadow: 1,
                "& .MuiDataGrid-row:last-child": {
                  fontWeight: 700,
                  borderTop: "3px solid",
                },
              }}
            />

            {/* GRID 2C — 3500 DIAS */}
            <Typography 
              variant="body1" 
              color="text.secondary" 
              sx={{ fontWeight: 700, mt: 3, mb: 1, display: "flex", alignItems: "center", gap: 1, }}
            >
              <RadioButtonCheckedIcon sx={{ fontSize: 18 }} />
              {t("calculations.methods.krijgsman-lobo-ferreira.results.distancia3500Dias")}
            </Typography>
            <DataGrid
              rows={gridDistance3500Rows(r)}
              columns={gridDistance3500Columns(t).map(c => ({ ...c, sortable: false }))}
              hideFooter
              disableColumnMenu
              sx={{
                boxShadow: 1,
                "& .MuiDataGrid-row:last-child": {
                  fontWeight: 700,
                  borderTop: "3px solid",
                },
              }}
            />
          </Box>
        );
      })}
    </Box>
  );
}